import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { UserPlus, KeyRound, Loader2, ShieldCheck, SlidersHorizontal, Trash2, Plus } from "lucide-react";

const selectCls = "h-9 rounded-md bg-slate-900 border border-slate-800 text-white text-sm px-2";

// Grade de permissoes agrupada. `value` = conjunto marcado; `onToggle(key, checked)`.
function PermGrid({ catalog, value, onToggle, base = null }) {
  const groups = useMemo(() => {
    const g = {};
    catalog.forEach((c) => { (g[c.group] = g[c.group] || []).push(c); });
    return Object.entries(g);
  }, [catalog]);
  return (
    <div className="grid md:grid-cols-2 gap-4">
      {groups.map(([name, items]) => (
        <div key={name}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">{name}</div>
          {items.map((c) => {
            const on = value.has(c.key);
            const delta = base ? (on && !base.has(c.key) ? "extra" : !on && base.has(c.key) ? "removida" : "") : "";
            return (
              <label key={c.key} className="flex items-start gap-2 py-1 cursor-pointer" data-testid={`perm-${c.key}`}>
                <input type="checkbox" checked={on} onChange={(e) => onToggle(c.key, e.target.checked)} className="mt-1" />
                <span>
                  <span className="text-sm text-white">{c.label}</span>
                  {delta && <Badge className={`ml-2 text-[10px] ${delta === "extra" ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>{delta}</Badge>}
                  <span className="block text-[11px] text-slate-500">{c.help}</span>
                </span>
              </label>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function MemberPerms({ member, role, catalog, onSaved }) {
  const base = useMemo(() => new Set(role?.permissions || []), [role]);
  const [grant, setGrant] = useState(new Set(member.perm_grant));
  const [deny, setDeny] = useState(new Set(member.perm_deny));
  const [saving, setSaving] = useState(false);
  const effective = useMemo(() => {
    const e = new Set([...base, ...grant]);
    deny.forEach((k) => e.delete(k));
    return e;
  }, [base, grant, deny]);

  const toggle = (key, on) => {
    const g = new Set(grant); const d = new Set(deny);
    if (on) { d.delete(key); if (!base.has(key)) g.add(key); }
    else { g.delete(key); if (base.has(key)) d.add(key); }
    setGrant(g); setDeny(d);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/team/${member.id}`, { perm_grant: [...grant], perm_deny: [...deny] });
      toast.success("Permissões salvas");
      onSaved();
    } catch (ex) { toast.error(formatApiError(ex)); } finally { setSaving(false); }
  };
  return (
    <div className="p-4 bg-slate-950/60 border-t border-slate-800">
      <p className="text-xs text-slate-500 mb-3">
        Parte do perfil <b className="text-slate-300">{member.role_name}</b>. Marque ou desmarque para abrir uma exceção só para {member.name}.
      </p>
      <PermGrid catalog={catalog} value={effective} onToggle={toggle} base={base} />
      <div className="mt-3 flex gap-2">
        <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950">
          {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />} Salvar exceções
        </Button>
        <Button size="sm" variant="ghost" onClick={() => { setGrant(new Set()); setDeny(new Set()); }} className="text-slate-400">Voltar ao padrão do perfil</Button>
      </div>
    </div>
  );
}

function RoleEditor({ role, catalog, onSaved, onDeleted }) {
  const [name, setName] = useState(role.name);
  const [perms, setPerms] = useState(new Set(role.permissions));
  const [saving, setSaving] = useState(false);
  const toggle = (k, on) => { const p = new Set(perms); on ? p.add(k) : p.delete(k); setPerms(p); };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/team/roles/${role.id}`, { name, permissions: [...perms] });
      toast.success("Perfil salvo"); onSaved();
    } catch (ex) { toast.error(formatApiError(ex)); } finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm(`Apagar o perfil "${role.name}"?`)) return;
    try { await api.delete(`/team/roles/${role.id}`); toast.success("Perfil apagado"); onDeleted(); }
    catch (ex) { toast.error(formatApiError(ex)); }
  };
  return (
    <Card className="bg-slate-900/60 border-slate-800 p-5" data-testid={`role-${role.id}`}>
      <div className="flex items-center gap-3 mb-3">
        <Input value={name} onChange={(e) => setName(e.target.value)} className="max-w-xs bg-slate-900 border-slate-800 text-white" />
        <Badge className="bg-slate-800 text-slate-300">{role.members} pessoa(s)</Badge>
        {role.builtin && <Badge className="bg-slate-800 text-slate-400">padrão</Badge>}
      </div>
      <PermGrid catalog={catalog} value={perms} onToggle={toggle} />
      <div className="mt-3 flex gap-2">
        <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950">
          {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />} Salvar perfil
        </Button>
        {!role.builtin && (
          <Button size="sm" variant="outline" onClick={del} className="border-slate-700 text-red-300"><Trash2 className="w-3.5 h-3.5 mr-1" /> Apagar</Button>
        )}
      </div>
    </Card>
  );
}

export default function Team() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ catalog: [], roles: [], limits: { reschedule_max_days: 3, reschedule_cooldown_days: 30 } });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", role: "attendant", password: "" });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [openPerms, setOpenPerms] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [limits, setLimits] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([api.get("/team"), api.get("/team/roles")]);
      setItems(m.data.items || []);
      setMeta(r.data);
      setLimits((cur) => cur || r.data.limits);
    } catch (ex) { toast.error(formatApiError(ex)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const roleOptions = [...meta.roles.map((r) => ({ value: r.id, label: r.name })), { value: "admin", label: "Administrador" }];
  const roleOf = (id) => meta.roles.find((r) => r.id === id);

  const create = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/team", form);
      setCreated({ email: form.email, password: data.generated_password || form.password });
      setForm({ name: "", email: "", role: "attendant", password: "" });
      load();
    } catch (ex) { toast.error(formatApiError(ex)); } finally { setSaving(false); }
  };
  const update = async (id, patch, okMsg) => {
    try { await api.put(`/team/${id}`, patch); toast.success(okMsg || "Salvo"); load(); }
    catch (ex) { toast.error(formatApiError(ex)); }
  };
  const resetPassword = async (m) => {
    const pwd = window.prompt(`Nova senha para ${m.name} (mínimo 8 caracteres):`);
    if (!pwd) return;
    try { await api.post(`/team/${m.id}/password`, { password: pwd }); toast.success("Senha alterada"); }
    catch (ex) { toast.error(formatApiError(ex)); }
  };
  const addRole = async () => {
    try { await api.post("/team/roles", { name: newRole, permissions: ["chat.reply"] }); setNewRole(""); toast.success("Perfil criado"); load(); }
    catch (ex) { toast.error(formatApiError(ex)); }
  };
  const saveLimits = async () => {
    try { await api.put("/team/limits", limits); toast.success("Limites salvos"); load(); }
    catch (ex) { toast.error(formatApiError(ex)); }
  };

  return (
    <div className="space-y-8" data-testid="team-page">
      <header className="border-b border-slate-800 pb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">Configurações</div>
        <h1 className="font-outfit text-3xl font-bold text-white mt-1">Equipe</h1>
        <p className="text-sm text-slate-400 mt-1">Cadastre pessoas, crie perfis e ajuste o que cada um pode fazer.</p>
      </header>

      <Tabs defaultValue="membros">
        <TabsList className="bg-slate-900/50 border border-slate-800">
          <TabsTrigger value="membros" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-emerald-950 text-slate-300">Pessoas</TabsTrigger>
          <TabsTrigger value="perfis" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-emerald-950 text-slate-300">Perfis e permissões</TabsTrigger>
        </TabsList>

        <TabsContent value="membros" className="mt-6 space-y-6">
          <Card className="bg-slate-900/60 border-slate-800 p-5">
            <div className="flex items-center gap-2 text-white font-medium mb-3"><UserPlus className="w-4 h-4" /> Novo membro</div>
            <div className="grid md:grid-cols-4 gap-3">
              <div><Label className="text-xs text-slate-400">Nome</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name" className="bg-slate-900 border-slate-800 text-white mt-1" /></div>
              <div><Label className="text-xs text-slate-400">E-mail (login)</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-email" className="bg-slate-900 border-slate-800 text-white mt-1" /></div>
              <div><Label className="text-xs text-slate-400">Perfil</Label>
                <div className="mt-1"><select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="team-role" className={selectCls}>
                  {roleOptions.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}</select></div></div>
              <div><Label className="text-xs text-slate-400">Senha inicial (vazio = gerar)</Label>
                <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="team-password" className="bg-slate-900 border-slate-800 text-white mt-1" /></div>
            </div>
            <Button onClick={create} disabled={saving || !form.name || !form.email} data-testid="team-create" className="mt-3 bg-emerald-500 hover:bg-emerald-400 text-emerald-950">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />} Cadastrar
            </Button>
            {created && (
              <div className="mt-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-100">
                Acesso criado. Passe estes dados para a pessoa (a senha não aparece de novo):<br />
                E-mail: <b className="font-mono-jb">{created.email}</b> · Senha: <b className="font-mono-jb">{created.password}</b>
              </div>
            )}
          </Card>

          <Card className="bg-slate-900/60 border-slate-800 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-800">
                <tr><th className="text-left p-3">Nome</th><th className="text-left p-3">Perfil</th><th className="text-left p-3">Último acesso</th><th className="p-3" /></tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={4} className="p-4 text-slate-500">Carregando…</td></tr>}
                {items.map((m) => {
                  const me = m.id === (user?.id || user?._id);
                  const custom = (m.perm_grant.length + m.perm_deny.length) > 0;
                  return (
                    <Fragment key={m.id}>
                      <tr className="border-b border-slate-800/60" data-testid={`team-row-${m.email}`}>
                        <td className="p-3">
                          <div className="text-white">{m.name} {me && <Badge className="ml-1 bg-slate-700 text-slate-200">você</Badge>}
                            {custom && <Badge className="ml-1 bg-amber-500/15 text-amber-300">exceções</Badge>}</div>
                          <div className="text-xs text-slate-500 font-mono-jb">{m.email}</div>
                        </td>
                        <td className="p-3">
                          <select value={m.role} disabled={me} onChange={(e) => update(m.id, { role: e.target.value }, "Perfil alterado")}
                            className="h-8 rounded-md bg-slate-900 border border-slate-800 text-white text-xs px-2 disabled:opacity-50">
                            {roleOptions.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                            {!roleOf(m.role) && m.role !== "admin" && <option value={m.role}>(perfil removido)</option>}
                          </select>
                        </td>
                        <td className="p-3 text-xs text-slate-400">{m.last_login ? new Date(m.last_login).toLocaleString("pt-BR") : "—"}</td>
                        <td className="p-3 text-right whitespace-nowrap">
                          {m.role !== "admin" && (
                            <Button size="sm" variant="ghost" onClick={() => setOpenPerms(openPerms === m.id ? null : m.id)} className="text-slate-400 hover:text-white">
                              <SlidersHorizontal className="w-3.5 h-3.5 mr-1" /> Permissões</Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => resetPassword(m)} className="text-slate-400 hover:text-white"><KeyRound className="w-3.5 h-3.5 mr-1" /> Senha</Button>
                          {!me && (
                            <Button size="sm" variant="outline" onClick={() => update(m.id, { active: !m.active }, m.active ? "Usuário desativado" : "Usuário reativado")}
                              className={`ml-1 border-slate-700 ${m.active ? "text-red-300" : "text-emerald-300"}`}>{m.active ? "Desativar" : "Reativar"}</Button>
                          )}
                          {!m.active && <Badge className="ml-2 bg-red-500/15 text-red-300 border border-red-500/40">inativo</Badge>}
                        </td>
                      </tr>
                      {openPerms === m.id && (
                        <tr><td colSpan={4} className="p-0">
                          <MemberPerms key={m.id + m.perm_grant.join() + m.perm_deny.join() + m.role} member={m} role={roleOf(m.role)} catalog={meta.catalog} onSaved={load} />
                        </td></tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="perfis" className="mt-6 space-y-6">
          <Card className="bg-slate-900/60 border-slate-800 p-5">
            <div className="flex items-center gap-2 text-white font-medium mb-2"><ShieldCheck className="w-4 h-4" /> Administrador</div>
            <p className="text-sm text-slate-400">Sempre tem acesso total (configurações, integrações, equipe, cobranças e bloqueios). Esse perfil não pode ser alterado nem concedido por permissão.</p>
          </Card>

          {meta.roles.map((r) => (
            <RoleEditor key={r.id + r.permissions.join()} role={r} catalog={meta.catalog} onSaved={load} onDeleted={load} />
          ))}

          <Card className="bg-slate-900/60 border-slate-800 p-5">
            <div className="text-white font-medium mb-2">Novo perfil</div>
            <div className="flex gap-2">
              <Input value={newRole} onChange={(e) => setNewRole(e.target.value)} placeholder='Ex.: "Financeiro"' data-testid="new-role-name" className="max-w-xs bg-slate-900 border-slate-800 text-white" />
              <Button onClick={addRole} disabled={!newRole.trim()} data-testid="new-role-create" className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950"><Plus className="w-4 h-4 mr-1" /> Criar</Button>
            </div>
          </Card>

          {limits && (
            <Card className="bg-slate-900/60 border-slate-800 p-5">
              <div className="text-white font-medium mb-1">Limites do agendamento de pagamento</div>
              <p className="text-xs text-slate-500 mb-3">Valem para quem tem “Agendar pagamento” mas não tem “Agendar sem limites”.</p>
              <div className="grid grid-cols-2 gap-3 max-w-md">
                <div><Label className="text-xs text-slate-400">Prazo máximo (dias à frente)</Label>
                  <Input type="number" min="0" max="60" value={limits.reschedule_max_days} data-testid="limit-days"
                    onChange={(e) => setLimits({ ...limits, reschedule_max_days: Number(e.target.value) })} className="bg-slate-900 border-slate-800 text-white mt-1" /></div>
                <div><Label className="text-xs text-slate-400">Intervalo entre agendamentos do mesmo cliente (dias)</Label>
                  <Input type="number" min="0" max="365" value={limits.reschedule_cooldown_days} data-testid="limit-cooldown"
                    onChange={(e) => setLimits({ ...limits, reschedule_cooldown_days: Number(e.target.value) })} className="bg-slate-900 border-slate-800 text-white mt-1" /></div>
              </div>
              <Button size="sm" onClick={saveLimits} className="mt-3 bg-emerald-500 hover:bg-emerald-400 text-emerald-950">Salvar limites</Button>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
